import React from 'react';
import Logo from './Logo';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-8">
          <Logo size="default" variant="glow" />
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-gray-600 italic mb-8">Effective Date: December 8, 2025</p>

          <div className="prose prose-lg max-w-none space-y-6 text-gray-800">
            <p>
              Stomp ("we," "us," or "our") respects your privacy and is committed to protecting your personal information. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our 
              streaming service advisor application and related services (collectively, the "Service").
            </p>
            <p>
              By using the Service, you consent to the data practices described in this Privacy Policy. If you do not agree 
              with this Privacy Policy, please do not use the Service.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">1.1 Information You Provide</h3>
            <p>We collect information you voluntarily provide when you:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>create an account (email address, name, password);</li>
              <li>set up your profile and preferences (streaming services you use, content preferences);</li>
              <li>provide credit card or membership information to identify available perks (card issuer, membership type—we do not store full credit card numbers);</li>
              <li>contact customer support; or</li>
              <li>respond to surveys or promotions.</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">1.2 Information Collected Automatically</h3>
            <p>When you use the Service, we automatically collect:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>device information (device type, operating system, unique device identifiers);</li>
              <li>usage data (features used, content viewed, search queries, time spent);</li>
              <li>log data (IP address, browser type, access times, pages viewed); and</li>
              <li>location data (general location based on IP address).</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">1.3 Information from Third Parties</h3>
            <p>We may receive information from third parties, including:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>authentication services if you sign in through Google, Apple, or other providers;</li>
              <li>payment processors regarding transaction status; and</li>
              <li>publicly available information about streaming services, pricing, and features.</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>provide, maintain, and improve the Service;</li>
              <li>personalize your experience and provide tailored recommendations;</li>
              <li>identify streaming perks available through your credit cards and memberships;</li>
              <li>process transactions and send related information;</li>
              <li>send administrative messages, updates, and promotional communications (with your consent);</li>
              <li>respond to your comments, questions, and support requests;</li>
              <li>monitor and analyze trends, usage, and activities;</li>
              <li>detect, investigate, and prevent fraudulent transactions and other illegal activities; and</li>
              <li>comply with legal obligations.</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. How We Share Your Information</h2>
            <p>We do not sell your personal information. We may share your information in the following circumstances:</p>

            <div className="space-y-4">
              <div>
                <p className="font-semibold">Service Providers:</p>
                <p>
                  We share information with third-party vendors who perform services on our behalf, including payment processing 
                  (Stripe), hosting, analytics, and customer support. These providers are contractually obligated to protect your 
                  information.
                </p>
              </div>

              <div>
                <p className="font-semibold">Business Transfers:</p>
                <p>
                  If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of 
                  that transaction.
                </p>
              </div>

              <div>
                <p className="font-semibold">Legal Requirements:</p>
                <p>
                  We may disclose information if required by law, regulation, legal process, or governmental request, or to protect 
                  the rights, property, or safety of us, our users, or others.
                </p>
              </div>

              <div>
                <p className="font-semibold">With Your Consent:</p>
                <p>We may share information with your consent or at your direction.</p>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Data Retention</h2>
            <p>
              We retain your personal information for as long as your account is active or as needed to provide you the Service. 
              We may retain certain information after account deletion for legitimate business purposes, including to comply with 
              legal obligations, resolve disputes, enforce our agreements, and as permitted by applicable law. You may request 
              deletion of your account and personal information as described in Section 6.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized 
              access, alteration, disclosure, or destruction. These measures include encryption of data in transit and at rest, regular 
              security assessments, access controls, and employee training. However, no method of transmission over the Internet or 
              electronic storage is completely secure, and we cannot guarantee absolute security.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Your Rights and Choices</h2>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">6.1 Account Information</h3>
            <p>
              You may update, correct, or delete your account information at any time by logging into your account settings or 
              contacting us. Note that we may retain certain information as required by law or for legitimate business purposes.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">6.2 Communications</h3>
            <p>
              You may opt out of promotional communications by following the unsubscribe instructions in those messages or updating 
              your preferences in your account settings. You will continue to receive transactional communications related to your 
              account and the Service.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">6.3 Cookies and Tracking</h3>
            <p>
              Most web browsers are set to accept cookies by default. You can usually modify your browser settings to remove or reject 
              cookies. Please note that removing or rejecting cookies could affect the availability and functionality of the Service.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">6.4 Data Portability and Access</h3>
            <p>
              You may request a copy of your personal information in a structured, commonly used, and machine-readable format. Contact 
              us at the email address below to make such a request.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. California Privacy Rights</h2>
            <p>
              If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA), including 
              the right to know what personal information we collect, the right to delete your personal information, the right to 
              opt-out of the sale of personal information (we do not sell personal information), and the right to non-discrimination 
              for exercising your privacy rights. To exercise these rights, contact us at the email address below.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of residence. These countries 
              may have data protection laws that are different from the laws of your country. We take appropriate safeguards to ensure 
              that your personal information remains protected in accordance with this Privacy Policy.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Children's Privacy</h2>
            <p>
              The Service is not intended for children under the age of 13 (or 16 in some jurisdictions). We do not knowingly collect 
              personal information from children under these ages. If we learn that we have collected personal information from a child 
              under the applicable age, we will take steps to delete that information promptly. If you believe we have collected 
              information from a child, please contact us.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Third-Party Links and Services</h2>
            <p>
              The Service may contain links to third-party websites, services, or applications. This Privacy Policy does not apply to 
              those third parties. We encourage you to review the privacy policies of any third-party services you access through or 
              in connection with the Service.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. If we make material changes, we will notify you by email or through 
              the Service prior to the changes taking effect. We encourage you to review this Privacy Policy periodically. Your continued 
              use of the Service after the effective date of the revised Privacy Policy constitutes your acceptance of the changes.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or our privacy practices, please contact us at{' '}
              <a href="mailto:privacy@stomp.app" className="text-purple-600 hover:text-purple-700 underline">
                privacy@stomp.app
              </a>
              .
            </p>
          </div>

          {/* Back Button */}
          <div className="mt-12 text-center">
            <button
              onClick={() => window.history.back()}
              className="inline-block bg-purple-600 hover:bg-purple-700 text-white font-semibold px-8 py-3 rounded-lg transition-colors"
            >
              Back to App
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;